// Lead Meetings tab — list scheduled Google Meets for the lead, with a
// "Schedule" button that opens CreateMeetingModal. Cancel marks the meeting
// canceled and best-effort deletes the underlying Google Calendar event.
//
// Pre-flight: meetings require the lead's project to have a connected Google
// account (see Settings → Projects → Google Meet). We surface a clear "connect
// first" state here so the user isn't dropped into a failing create flow.

import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Video, Plus, Loader2, Calendar, ExternalLink, Trash2, AlertCircle,
  CheckCircle2, Link as LinkIcon, Settings as SettingsIcon,
  Sparkles, FileText, ChevronDown, ChevronRight, ChevronUp, RefreshCw, Film, Download, Play,
} from "lucide-react";
import toast from "react-hot-toast";
import { Button, IconButton, Badge, Textarea } from "../../atoms";
import {
  useLeadMeetings, useCancelLeadMeeting, useSetMeetingTranscript,
  useStartMeetingBot, usePullVexaTranscript, useCheckMeetingEnded,
  useRegenerateMeetingSummary, useLeadMeetingsRollup, useGenerateLeadMeetingsRollup,
} from "../../hooks/useLeadMeetings";
import { useProjectMeetAccount } from "../../hooks/useProjectMeetAccount";
import { useUserMeetAccount, useStartUserMeetConnect } from "../../hooks/useUserMeetAccount";
import CreateMeetingModal from "../../overlays/CreateMeetingModal";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";
import useFocusRow, { FOCUS_ROW_STYLE } from "../../hooks/useFocusRow";
import { splitMeetingsByState } from "../../utils/meetingState";

const STATUS_TONE = {
  scheduled: { bg: "var(--crm-primary-soft)", fg: "var(--crm-primary)" },
  completed: { bg: "var(--crm-success-soft)", fg: "var(--crm-success)" },
  canceled:  { bg: "var(--color-bg-muted)",   fg: "var(--color-text-secondary)" },
};

function formatRange(start, end) {
  if (!start) return "—";
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const date = s.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const sTime = s.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const eTime = e ? e.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : null;
  return eTime ? `${date} · ${sTime} – ${eTime}` : `${date} · ${sTime}`;
}

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || STATUS_TONE.scheduled;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "2px 8px", borderRadius: 999,
      fontSize: 10, fontWeight: 600, background: tone.bg, color: tone.fg,
    }}>{status}</span>
  );
}

// Visual indicator for the auto-recording bot's lifecycle. Idle = nothing
// shown (don't clutter the row when the feature isn't engaged); other states
// surface so the user can tell whether the bot is on its way, listening,
// done, or stuck.
const VEXA_TONE = {
  requested: { bg: "var(--color-bg-muted)",       fg: "var(--color-text-secondary)", label: "Bot queued" },
  joining:   { bg: "var(--crm-primary-soft)",     fg: "var(--crm-primary)",          label: "Bot joining" },
  recording: { bg: "var(--crm-primary-soft)",     fg: "var(--crm-primary)",          label: "Bot recording" },
  completed: { bg: "var(--crm-success-soft)",     fg: "var(--crm-success)",          label: "Bot done" },
  failed:    { bg: "var(--crm-danger-soft)",      fg: "var(--crm-danger)",           label: "Bot failed" },
};
function VexaBotPill({ bot }) {
  const status = bot?.status;
  if (!status || status === "idle") return null;
  const tone = VEXA_TONE[status];
  if (!tone) return null;
  return (
    <span
      title={bot?.lastError || tone.label}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 999,
        fontSize: 10, fontWeight: 600, background: tone.bg, color: tone.fg,
      }}
    >
      🤖 {tone.label}
    </span>
  );
}

// Tiny 🔁 badge shown on recurring occurrences. Derives a human label from the
// stored RRULE so we don't need extra fields on the wire.
function recurrenceLabel(rule) {
  if (!rule) return "Repeats";
  const u = String(rule).toUpperCase();
  if (u.includes("FREQ=DAILY")) return "Daily";
  if (u.includes("BYDAY=MO,TU,WE,TH,FR")) return "Weekdays";
  if (u.includes("FREQ=WEEKLY")) return "Weekly";
  return "Repeats";
}
function RecurringBadge({ rule }) {
  return (
    <span
      title={rule || "Recurring meeting"}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 999,
        fontSize: 10, fontWeight: 600, background: "var(--color-bg-muted)", color: "var(--color-text-secondary)",
      }}
    >
      🔁 {recurrenceLabel(rule)}
    </span>
  );
}

const SENTIMENT_TONE = {
  positive: { bg: "var(--crm-success-soft)", fg: "var(--crm-success)", label: "Positive" },
  neutral:  { bg: "var(--color-bg-muted)",   fg: "var(--color-text-secondary)", label: "Neutral" },
  negative: { bg: "var(--crm-danger-soft)",  fg: "var(--crm-danger)",  label: "Negative" },
};

function AiSummaryBlock({ leadId, meeting }) {
  const setTranscript = useSetMeetingTranscript(leadId);
  const regen = useRegenerateMeetingSummary(leadId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  // Summary is the primary view; the raw transcript is one click away.
  const [view, setView] = useState("summary");

  const ai = meeting.aiSummary || {};
  const hasSummary = !!ai.summary;
  const hasTranscript = typeof meeting.transcript === "string" && meeting.transcript.trim().length > 0;
  // "Generating" must be BOUNDED — otherwise a meeting that has a transcript but
  // whose summary silently failed / was never triggered spins forever (that was
  // the bug: old meetings stuck on "generating…"). A summary that's genuinely
  // in progress lands within ~2 min of the transcript being set. So we only
  // treat it as generating while: a mutation is in flight, OR the transcript
  // was updated very recently. Past that window, with no summary and no error,
  // it's "stuck" → we offer a Generate button instead of an infinite spinner.
  const GENERATING_WINDOW_MS = 2 * 60 * 1000;
  const tsUpdated = meeting.transcriptUpdatedAt ? new Date(meeting.transcriptUpdatedAt).getTime() : 0;
  const withinWindow = tsUpdated > 0 && (Date.now() - tsUpdated) < GENERATING_WINDOW_MS;
  const generating =
    setTranscript.isPending ||
    regen.isPending ||
    (hasTranscript && !hasSummary && !ai.error && withinWindow);
  // Transcript present, no summary, not actively generating, no error → the
  // summary never completed. Let the user kick it off rather than spin forever.
  const stuckNoSummary = hasTranscript && !hasSummary && !ai.error && !generating;

  useEffect(() => {
    if (editing) setDraft(meeting.transcript || "");
  }, [editing, meeting.transcript]);

  const onSave = async () => {
    const text = draft.trim();
    if (!text) {
      toast.error("Paste a transcript before saving");
      return;
    }
    try {
      await setTranscript.mutateAsync({ meetingId: meeting._id, transcript: text });
      toast.success("Transcript saved — AI summary generating");
      setEditing(false);
      setOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const sentimentTone = SENTIMENT_TONE[ai.sentiment] || null;

  return (
    <div style={{
      marginTop: 10, borderRadius: 8,
      border: "1px solid var(--color-border-subtle)",
      background: "var(--color-bg-base)",
      overflow: "hidden",
    }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          padding: "10px 12px",
          background: hasSummary ? "var(--crm-primary-soft)" : "transparent",
          border: "none", cursor: "pointer", textAlign: "left",
        }}
      >
        <div style={{
          width: 24, height: 24, borderRadius: 9999, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: hasSummary ? "var(--crm-primary)" : "var(--color-bg-muted)",
          color: hasSummary ? "#fff" : "var(--color-text-secondary)",
        }}>
          <Sparkles size={12} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text-primary)" }}>
              AI summary
            </span>
            {generating && (
              <span style={{ fontSize: 11, color: "var(--color-text-secondary)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Loader2 size={11} className="animate-spin" /> generating…
              </span>
            )}
            {hasSummary && sentimentTone && (
              <span style={{
                padding: "1px 7px", borderRadius: 999,
                fontSize: 10, fontWeight: 600, background: sentimentTone.bg, color: sentimentTone.fg,
              }}>
                {sentimentTone.label}
              </span>
            )}
          </div>
          {/* Glanceable preview so the summary reads at a glance without expanding —
              matches the Master summary banner's title+subtitle layout. */}
          <div style={{
            marginTop: 2, fontSize: 11, color: "var(--color-text-secondary)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {hasSummary
              ? ai.summary
              : !hasTranscript
                ? "No transcript yet — Sync now or paste one below"
                : generating
                  ? "Generating…"
                  : "Transcript ready — summary pending"}
          </div>
        </div>

        {open ? <ChevronDown size={13} style={{ flexShrink: 0 }} /> : <ChevronRight size={13} style={{ flexShrink: 0 }} />}
      </button>

      {open && (
        <div style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
          {ai.error && (
            <div style={{
              padding: 10, borderRadius: 8,
              background: "var(--crm-danger-soft)", color: "var(--crm-danger)",
              fontSize: 12, display: "flex", alignItems: "flex-start", gap: 8,
            }}>
              <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>Couldn't generate summary: {ai.error}</span>
            </div>
          )}

          {/* Transcript exists but no summary and nothing running → let the
              user trigger it instead of an infinite "generating…" spinner. */}
          {stuckNoSummary && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                Transcript saved — summary not generated yet.
              </span>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await regen.mutateAsync(meeting._id);
                    toast.success("Generating summary…");
                  } catch (err) {
                    toast.error(err?.response?.data?.error || err.message || "Failed");
                  }
                }}
                disabled={regen.isPending}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "5px 10px", borderRadius: 8, border: "none",
                  background: "var(--crm-primary)", color: "#fff",
                  fontSize: 11, fontWeight: 600, cursor: regen.isPending ? "wait" : "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <Sparkles size={11} /> Generate summary
              </button>
            </div>
          )}

          {(hasSummary || hasTranscript) && (
            <div style={{ display: "flex", gap: 4 }}>
              <ViewTab active={view === "summary"} onClick={() => setView("summary")}>
                Summary
              </ViewTab>
              <ViewTab active={view === "transcript"} onClick={() => setView("transcript")} disabled={!hasTranscript}>
                Transcript
              </ViewTab>
            </div>
          )}

          {view === "transcript" && hasTranscript && (
            <pre style={{
              margin: 0, padding: 10, borderRadius: 8, maxHeight: 340, overflow: "auto",
              background: "var(--color-bg-muted)", color: "var(--color-text-primary)",
              fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word",
              fontFamily: "inherit",
            }}>
              {meeting.transcript}
            </pre>
          )}

          {view === "summary" && hasSummary && (
            <>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "var(--color-text-primary)" }}>
                {ai.summary}
              </p>
              {ai.description && (
                <div>
                  <div style={summaryHeading}>Details</div>
                  <p style={{
                    margin: 0, fontSize: 12, lineHeight: 1.6,
                    color: "var(--color-text-primary)", whiteSpace: "pre-wrap",
                  }}>
                    {ai.description}
                  </p>
                </div>
              )}
              {Array.isArray(ai.keyPoints) && ai.keyPoints.length > 0 && (
                <div>
                  <div style={summaryHeading}>Key points</div>
                  <ul style={summaryList}>
                    {ai.keyPoints.map((p, i) => <li key={i} style={summaryItem}>{p}</li>)}
                  </ul>
                </div>
              )}
              {Array.isArray(ai.actionItems) && ai.actionItems.length > 0 && (
                <div>
                  <div style={summaryHeading}>Action items</div>
                  <ul style={summaryList}>
                    {ai.actionItems.map((p, i) => <li key={i} style={summaryItem}>{p}</li>)}
                  </ul>
                </div>
              )}
              {ai.nextSteps && (
                <div>
                  <div style={summaryHeading}>Next steps</div>
                  <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: "var(--color-text-primary)" }}>
                    {ai.nextSteps}
                  </p>
                </div>
              )}
              {ai.generatedAt && (
                <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                  Generated {new Date(ai.generatedAt).toLocaleString()}
                  {ai.model ? ` · ${ai.model}` : ""}
                </div>
              )}
            </>
          )}

          {!hasSummary && !generating && !ai.error && (
            <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
              Paste a transcript below and we'll generate a structured summary.
            </div>
          )}

          {editing ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Paste the meeting transcript here…"
                rows={8}
                style={{ minHeight: 160, resize: "vertical" }}
              />
              <div style={{ display: "flex", gap: 8 }}>
                <Button size="sm" onClick={onSave} disabled={setTranscript.isPending}>
                  {setTranscript.isPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  <span style={{ marginLeft: 6 }}>Save & summarize</span>
                </Button>
                <Button size="sm" variant="secondary" onClick={() => { setEditing(false); setDraft(""); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button size="sm" variant="secondary" leftIcon={<FileText size={12} />} onClick={() => setEditing(true)}>
                {hasTranscript ? "Update transcript" : "Add transcript"}
              </Button>
              {hasTranscript && (hasSummary || ai.error) && (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={regen.isPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  onClick={async () => {
                    try {
                      await regen.mutateAsync(meeting._id);
                      toast.success("Re-summarized");
                    } catch (err) {
                      toast.error(err?.response?.data?.error || err.message || "Re-summarize failed");
                    }
                  }}
                  disabled={regen.isPending}
                  title="Re-generate the AI summary from the current transcript"
                >
                  {regen.isPending ? "Re-summarizing…" : "Re-summarize"}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Segmented control for switching the AI block between the generated summary
// and the raw transcript it came from.
function ViewTab({ active, disabled, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "4px 10px", borderRadius: 999, border: "none",
        fontSize: 11, fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        background: active ? "var(--crm-primary-soft)" : "transparent",
        color: active ? "var(--crm-primary)" : "var(--color-text-secondary)",
      }}
    >
      {children}
    </button>
  );
}

const summaryHeading = {
  fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 4,
};
const summaryList = { margin: 0, paddingLeft: 18 };
const summaryItem = { fontSize: 12, lineHeight: 1.5, color: "var(--color-text-primary)", marginBottom: 2 };

function MeetingRow({ leadId, meeting, rowRef }) {
  const cancel = useCancelLeadMeeting(leadId);
  const startBot = useStartMeetingBot(leadId);
  const pullVexa = usePullVexaTranscript(leadId);
  const checkEnded = useCheckMeetingEnded(leadId);
  const isCanceled = meeting.status === "canceled";
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const botStatus = meeting?.vexaBot?.status || "idle";
  // Recurring meeting: the bot can be (re)dispatched on EVERY join — each join
  // is its own session, the link is reused, and the meeting never goes
  // "completed". So don't gate on vexaBot.status (which is unused for recurring)
  // or on completed status. One-off keeps the original guard.
  const canStartBot = meeting.isRecurring
    ? !isCanceled
    : (!isCanceled
      && meeting.status !== "completed"
      && (botStatus === "idle" || botStatus === "failed"));

  const hasTranscript = !!meeting.transcript;
  // Sync only makes sense for a meeting that has plausibly run — bot was
  // engaged or the start time passed — and isn't already wrapped up. The
  // background poll job (server/utils/cronJobs.js) usually closes this out on
  // its own within a minute of the bot finishing; this button is the manual
  // "do it now" override, so one click does both steps instead of two.
  const meetingStart = meeting.scheduledStart ? new Date(meeting.scheduledStart) : null;
  const meetingHasStarted = meetingStart && meetingStart.getTime() <= Date.now();
  const botEverEngaged = botStatus !== "idle";
  const canSync = !isCanceled && !hasTranscript && !!meeting.meetUri
    && (meetingHasStarted || botEverEngaged);
  const syncing = checkEnded.isPending || pullVexa.isPending;

  const onSyncNow = async () => {
    try {
      const res = await checkEnded.mutateAsync(meeting._id);
      if (!res.ended) {
        const msg = res.reason === "still_live"
          ? "Meeting is still ongoing"
          : res.reason === "no_conference_record_yet"
            ? "Meeting just ended? Google takes a few minutes to confirm — try again shortly"
            : res.reason === "no_meeting_code"
              ? "This meeting has no Meet link to check"
              : "Meeting not ended yet";
        toast(msg, { icon: "ℹ️" });
        return;
      }
      await pullVexa.mutateAsync({ meetingId: meeting._id, manual: true });
      toast.success("Synced — transcript & recording on the way");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Sync failed");
    }
  };

  // Join + Start Bot in one click. The Meet tab opens regardless of bot
  // outcome — we don't want to block the user from joining their own call
  // because Vexa is unreachable. Bot dispatch is fire-and-forget when it's
  // already in flight (idempotent on the backend).
  const onJoinMeet = async () => {
    // Pop the tab synchronously so the browser counts this as a user gesture
    // (otherwise popup blockers eat the window.open after an await).
    const meetTab = window.open(meeting.meetUri, "_blank", "noopener,noreferrer");
    if (canStartBot) {
      try {
        await startBot.mutateAsync(meeting._id);
        toast.success("Bot dispatched — joining the meeting");
      } catch (err) {
        // Soft error: user is already in the meeting; surface the issue but
        // don't make a big deal of it.
        toast.error(err?.response?.data?.error || err.message || "Bot failed to start");
      }
    }
    // Re-focus the Meet tab in case the toast pulled focus back.
    if (meetTab) meetTab.focus?.();
  };
  const recording = meeting.recording || {};
  const hasRecording = recording.status === "available" && (recording.drivePreviewUrl || recording.driveViewUrl);
  // Vexa-side recording (audio). Streamed through our backend proxy so we can
  // keep the Vexa API key server-side. Same expand/collapse pattern as Drive.
  const vexaRec = meeting.vexaRecording || {};
  const hasVexaRecording = vexaRec.status === "available" && vexaRec.recordingId && vexaRec.mediaFileId;
  // Recording is still being finalised on Vexa's side (chunked WebM upload not
  // done yet). We deliberately don't expose play/download for a partial file —
  // show a "processing" hint instead so the button isn't just missing.
  // Gate it on the bot having actually run: a meeting that's still "scheduled"
  // with an idle bot shouldn't show "Audio processing…" (that'd be a confusing
  // state contradiction — scheduled but processing audio).
  const botEngaged = ["recording", "completed", "joining"].includes(meeting.vexaBot?.status)
    || meeting.status === "completed";
  const vexaRecPending = !hasVexaRecording && vexaRec.status === "pending" && botEngaged;
  const vexaAudioApiPath = hasVexaRecording
    ? `/leads/${leadId}/meetings/${meeting._id}/vexa-recording`
    : null;
  // Direct streaming URL for the inline player. We pass the JWT as ?token= so
  // <video>/<audio> src= can authenticate (it can't send the Bearer header).
  // This lets the browser STREAM via range requests instead of us downloading
  // the whole file into a blob first — essential for big mp4 recordings, which
  // otherwise blew past the 15s axios timeout. Recording is mp4 (video) when
  // the stored mime says so; the <video> tag streams + seeks natively.
  const vexaIsVideo = String(vexaRec.mimeType || "").includes("video")
    || String(vexaRec.mimeType || "").includes("mp4");
  const vexaStreamUrl = hasVexaRecording
    ? `/api/v1/crm${vexaAudioApiPath}?token=${encodeURIComponent(localStorage.getItem("token") || "")}`
    : null;
  // One "Recording" toggle covers BOTH sources (bot audio takes priority — it's
  // the primary pipeline; Drive/native Meet recording is the legacy fallback,
  // and the two rarely coexist on the same meeting). Collapsed by default:
  // recordings run 30+ min and shouldn't eat a player's worth of space on
  // every row by default.
  const hasAnyRecording = hasVexaRecording || hasRecording;
  const [recordingOpen, setRecordingOpen] = useState(false);

  // Download via a direct authenticated URL (?download=1 flips the backend's
  // Content-Disposition to attachment; ?token= carries auth). No blob, no
  // in-memory buffering — the browser downloads straight from the stream proxy,
  // so big mp4 recordings can't time out the way the old arraybuffer path did.
  const onDownloadVexa = () => {
    if (!vexaStreamUrl) return;
    const a = document.createElement("a");
    a.href = `${vexaStreamUrl}&download=1`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const isRecurring = !!meeting.isRecurring;

  const onCancel = () => setConfirmCancelOpen(true);

  const performCancel = async () => {
    try {
      const result = await cancel.mutateAsync(meeting._id);
      setConfirmCancelOpen(false);
      if (result?.meeting?.calendarError) {
        toast.success(isRecurring ? "Recurring meeting canceled" : "Meeting canceled");
        toast.error(`Google Calendar event could not be deleted: ${result.meeting.calendarError}. Remove it manually.`, { duration: 8000 });
      } else {
        toast.success(isRecurring ? "Recurring meeting canceled" : "Meeting canceled");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  return (
    <li ref={rowRef} style={{
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 8,
      background: "var(--color-bg-surface)",
      padding: 14,
      display: "flex", flexDirection: "column", gap: 0,
      opacity: isCanceled ? 0.7 : 1,
    }}>
    <div style={{ display: "flex", gap: 12 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 9999, flexShrink: 0,
        background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Video size={16} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{
            fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            textDecoration: isCanceled ? "line-through" : "none",
          }}>
            {meeting.title || "Meeting"}
          </span>
          <StatusPill status={meeting.status || "scheduled"} />
          {!isRecurring && <VexaBotPill bot={meeting.vexaBot} />}
          {isRecurring && <RecurringBadge rule={meeting.recurrenceRule} />}
        </div>

        <div style={{ marginTop: 3, fontSize: 11, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Calendar size={11} />
          <span>{formatRange(meeting.scheduledStart, meeting.scheduledEnd)}</span>
          {meeting.connectionEmail && <span>· {meeting.connectionEmail}</span>}
        </div>

        {Array.isArray(meeting.attendees) && meeting.attendees.length > 0 && (
          <div style={{ marginTop: 4, fontSize: 11, color: "var(--color-text-secondary)" }}>
            {meeting.attendees.join(", ")}
          </div>
        )}

        {meeting.description && (
          <div style={{
            marginTop: 8, padding: 10, borderRadius: 8,
            background: "var(--color-bg-muted)",
            fontSize: 12, lineHeight: "18px", color: "var(--color-text-primary)",
            whiteSpace: "pre-wrap",
          }}>
            {meeting.description}
          </div>
        )}

        <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {meeting.meetUri && !isCanceled && (
            <button
              type="button"
              onClick={onJoinMeet}
              disabled={startBot.isPending}
              title={canStartBot ? "Opens Meet and sends the recording bot" : "Opens Meet"}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "5px 10px", borderRadius: 8, border: "none",
                background: "var(--crm-primary)", color: "#fff",
                fontSize: 11, fontWeight: 600,
                cursor: startBot.isPending ? "wait" : "pointer",
                opacity: startBot.isPending ? 0.85 : 1,
              }}
            >
              {startBot.isPending ? <Loader2 size={11} className="animate-spin" /> : <Video size={11} />}
              Join Meet
            </button>
          )}
          {hasAnyRecording && (
            <button
              type="button"
              onClick={() => setRecordingOpen((v) => !v)}
              title={recordingOpen ? "Hide player" : (vexaIsVideo || (!hasVexaRecording && hasRecording)) ? "Play the recording" : "Play the audio"}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "5px 10px", borderRadius: 8, border: "1px solid var(--crm-primary)",
                background: recordingOpen ? "var(--crm-primary)" : "var(--crm-primary-soft)",
                color: recordingOpen ? "#fff" : "var(--crm-primary)",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}
            >
              {vexaIsVideo || (!hasVexaRecording && hasRecording)
                ? <Film size={11} />
                : <Play size={11} fill="currentColor" />}
              {vexaIsVideo || (!hasVexaRecording && hasRecording) ? "Play recording" : "Play audio"}
              {recordingOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            </button>
          )}
          {vexaRecPending && !hasAnyRecording && (
            <span
              title="Still finalising the recording — Sync in a minute"
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "5px 10px", borderRadius: 8,
                border: "1px solid var(--color-border-subtle)",
                background: "var(--color-bg-subtle)", color: "var(--color-text-secondary)",
                fontSize: 11, fontWeight: 600,
              }}
            >
              <RefreshCw size={11} /> Audio processing…
            </span>
          )}
          {canSync && (
            <button
              onClick={onSyncNow}
              disabled={syncing}
              title="Check whether the call has ended and pull the transcript + recording"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "5px 10px", borderRadius: 8, border: "1px solid var(--color-border-subtle)",
                background: "var(--color-bg-base)", color: "var(--color-text-secondary)",
                fontSize: 11, fontWeight: 600,
                cursor: syncing ? "wait" : "pointer",
                opacity: syncing ? 0.7 : 1,
              }}
            >
              {syncing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Sync now
            </button>
          )}
          {/* Secondary, low-frequency links — icon-only to keep the row from
              turning into a wall of buttons. Title carries the label. */}
          {meeting.googleCalendarHtmlLink && (
            <a
              href={meeting.googleCalendarHtmlLink}
              target="_blank"
              rel="noreferrer"
              title="Open in Google Calendar"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 28, height: 28, borderRadius: 8,
                background: "var(--color-bg-base)", border: "1px solid var(--color-border-subtle)",
                color: "var(--color-text-secondary)",
              }}
            >
              <ExternalLink size={12} />
            </a>
          )}
          {meeting.meetUri && (
            <button
              onClick={() => { navigator.clipboard?.writeText(meeting.meetUri); toast.success("Link copied"); }}
              title="Copy Meet link"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 28, height: 28, borderRadius: 8, border: "1px solid var(--color-border-subtle)",
                background: "var(--color-bg-base)", color: "var(--color-text-secondary)",
                cursor: "pointer",
              }}
            >
              <LinkIcon size={12} />
            </button>
          )}
        </div>
      </div>

      {!isCanceled && (
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <IconButton size="sm" aria-label="Cancel meeting" onClick={onCancel} disabled={cancel.isPending}>
            <Trash2 size={13} />
          </IconButton>
        </div>
      )}
    </div>

    {/* Bot recording takes priority when both exist (rare) — it's the primary
        pipeline going forward; Drive/native Meet recording only shows when
        there's no bot recording to fall back to. */}
    {!hasVexaRecording && hasRecording && recordingOpen && (
      <div style={{
        marginTop: 10, borderRadius: 8, overflow: "hidden",
        border: "1px solid var(--color-border-subtle)",
        background: "var(--color-bg-base)",
      }}>
        {/* Drive's `/preview` URL is an embeddable player with play/pause/seek
            controls baked in — same component source CRM used. iframe is the
            only sanctioned way to embed it; Drive blocks direct <video src=>. */}
        <iframe
          title={`Recording: ${meeting.title || "Meeting"}`}
          src={recording.drivePreviewUrl || recording.driveViewUrl}
          allow="autoplay; encrypted-media; fullscreen"
          allowFullScreen
          style={{
            display: "block",
            width: "100%",
            height: 360,
            border: "none",
            background: "#000",
          }}
        />
        {recording.driveViewUrl && (
          <div style={{
            padding: "6px 10px", display: "flex", justifyContent: "flex-end",
            borderTop: "1px solid var(--color-border-subtle)",
          }}>
            <a
              href={recording.driveViewUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 11, color: "var(--color-text-secondary)",
                textDecoration: "none",
              }}
            >
              <ExternalLink size={11} /> Open in Drive
            </a>
          </div>
        )}
      </div>
    )}

    {hasVexaRecording && recordingOpen && (
      <div style={{
        marginTop: 10, padding: 12,
        borderRadius: 8,
        border: "1px solid var(--color-border-subtle)",
        background: "var(--color-bg-base)",
        display: "flex", flexDirection: "column", gap: 8,
      }}>
        {/* Stream directly from the backend (src=...?token=JWT) so the browser
            uses range requests instead of us downloading the whole file first —
            no more 15s-timeout on big mp4 videos. mp4 → <video> (you see the
            meeting screen); audio-only → <audio>. */}
        {vexaIsVideo ? (
          <video
            controls
            preload="metadata"
            src={vexaStreamUrl || undefined}
            style={{ width: "100%", maxHeight: 360, borderRadius: 8, background: "#000" }}
          >
            Your browser does not support video playback.
          </video>
        ) : (
          <audio
            controls
            preload="metadata"
            src={vexaStreamUrl || undefined}
            style={{ width: "100%" }}
          >
            Your browser does not support audio playback.
          </audio>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "var(--color-text-secondary)" }}>
          <span>
            🎙️ Vexa audio
            {vexaRec.durationSec ? ` · ${Math.round(vexaRec.durationSec / 60)} min` : ""}
            {vexaRec.sizeBytes ? ` · ${(vexaRec.sizeBytes / 1024 / 1024).toFixed(1)} MB` : ""}
          </span>
          <button
            type="button"
            onClick={onDownloadVexa}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              color: "var(--color-text-secondary)", background: "transparent",
              border: "none", cursor: "pointer", padding: 0,
            }}
          >
            <Download size={11} /> Download
          </button>
        </div>
      </div>
    )}

    {/* One-off meeting → the existing single transcript/summary block.
        Recurring meeting → a list of session cards (each join's recording +
        transcript + summary), so reused-link joins never overwrite each other. */}
    {isRecurring
      ? <SessionList leadId={leadId} meeting={meeting} />
      : <AiSummaryBlock leadId={leadId} meeting={meeting} />}

    <ConfirmDeleteModal
      open={confirmCancelOpen}
      onClose={() => setConfirmCancelOpen(false)}
      onConfirm={performCancel}
      title={isRecurring ? "Cancel recurring meeting?" : "Cancel meeting?"}
      message={isRecurring
        ? `"${meeting.title || "Untitled meeting"}" recurs on one shared Meet link. Canceling deletes the recurring Google Calendar event and notifies attendees. Past session recordings stay on the lead.`
        : `"${meeting.title || "Untitled meeting"}" will be canceled and the Google Calendar event will be deleted. Attendees will be notified.`}
      confirmLabel={isRecurring ? "Cancel recurring meeting" : "Cancel meeting"}
      isPending={cancel.isPending}
    />
    </li>
  );
}

// Sessions list for a recurring meeting. Each join on the shared Meet link is a
// session with its own recording + transcript + AI summary. Newest first.
function SessionList({ leadId, meeting }) {
  const sessions = Array.isArray(meeting.sessions) ? meeting.sessions : [];
  if (sessions.length === 0) {
    return (
      <div style={{
        marginTop: 10, padding: "10px 12px", borderRadius: 8,
        background: "var(--color-bg-muted)", fontSize: 12, color: "var(--color-text-secondary)",
      }}>
        No sessions yet. Click <strong>Join Meet</strong> when the meeting starts — each time someone
        joins this link, the bot records that session here.
      </div>
    );
  }
  const ordered = [...sessions].sort(
    (a, b) => new Date(b.startedAt || 0) - new Date(a.startedAt || 0),
  );
  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
      {ordered.map((s, i) => (
        <SessionCard
          key={s._id || i}
          leadId={leadId}
          meeting={meeting}
          session={s}
          label={`Session ${ordered.length - i}`}
        />
      ))}
    </div>
  );
}

// One session card — start time, status, recording player, and its AI summary.
// The recording endpoint needs the auth header, so (like the one-off player) we
// fetch it as an arraybuffer and wrap it in a blob URL rather than pointing
// <audio src> at the API path directly.
function SessionCard({ leadId, meeting, session, label }) {
  const ai = session.aiSummary || {};
  const rec = session.vexaRecording || {};
  const hasRec = rec.status === "available" && rec.recordingId;
  // Direct streaming URL (token in query — <video>/<audio> can't send headers).
  // Browser streams via range requests; no blob download, no 15s timeout.
  const streamUrl = hasRec
    ? `/api/v1/crm/leads/${leadId}/meetings/${meeting._id}/vexa-recording?sessionId=${session._id}&token=${encodeURIComponent(localStorage.getItem("token") || "")}`
    : null;
  const isVideo = String(rec.mimeType || "").includes("video") || String(rec.mimeType || "").includes("mp4");

  const [playing, setPlaying] = useState(false);

  const tone = session.status === "completed" ? VEXA_TONE.completed
    : session.status === "failed" ? VEXA_TONE.failed
      : VEXA_TONE.recording;

  return (
    <div style={{
      border: "1px solid var(--color-border-subtle)", borderRadius: 8,
      background: "var(--color-bg-muted)", padding: 12,
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
          {session.startedAt ? formatRange(session.startedAt, session.endedAt) : "—"}
        </span>
        <span style={{
          marginLeft: "auto",
          display: "inline-flex", alignItems: "center", gap: 4,
          padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600,
          background: tone.bg, color: tone.fg,
        }}>🤖 {tone.label}</span>
      </div>

      {hasRec && (
        playing ? (
          isVideo ? (
            <video controls autoPlay preload="metadata" src={streamUrl}
              style={{ width: "100%", maxHeight: 320, borderRadius: 8, background: "#000" }} />
          ) : (
            <audio controls autoPlay preload="metadata" style={{ width: "100%", height: 34 }} src={streamUrl} />
          )
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start",
              padding: "5px 10px", borderRadius: 8, border: "1px solid var(--crm-primary)",
              background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
              fontSize: 11, fontWeight: 600, cursor: "pointer",
            }}
          >
            {isVideo ? <Film size={11} /> : <Play size={11} fill="currentColor" />}
            {isVideo ? "Play recording" : "Play audio"}
          </button>
        )
      )}

      {ai.summary ? (
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 6,
          fontSize: 12, lineHeight: "18px", color: "var(--color-text-primary)", whiteSpace: "pre-wrap",
        }}>
          <Sparkles size={12} style={{ color: "var(--crm-primary)", flexShrink: 0, marginTop: 2 }} />
          <span>{ai.summary}</span>
        </div>
      ) : ai.error ? (
        <div style={{ fontSize: 11, color: "var(--crm-danger)" }}>Summary failed: {ai.error}</div>
      ) : session.transcript ? (
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", display: "flex", alignItems: "center", gap: 6 }}>
          <Loader2 size={11} className="animate-spin" /> Generating summary…
        </div>
      ) : session.status === "recording" ? (
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>Recording… transcript appears after the session ends.</div>
      ) : (
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>No transcript captured for this session.</div>
      )}
    </div>
  );
}

// Master "all-meetings" rollup card. Same shape and behaviour as the calls
// RollupSummaryCard — sits above the meetings list and lets the user
// generate / refresh a single overview across every meeting on the lead.
function MeetingsRollupCard({ leadId, meetings }) {
  const { data } = useLeadMeetingsRollup(leadId);
  const gen = useGenerateLeadMeetingsRollup(leadId);
  const [open, setOpen] = useState(false);

  // Guard against non-string values: a recurring meeting keeps its transcript
  // per-session (top-level transcript may be absent/non-string), and migrated
  // docs can carry odd shapes. Coercing to string keeps .trim() from throwing
  // ("e.transcript.trim is not a function") and breaking the whole tab.
  const hasText = (v) => typeof v === "string" && v.trim().length > 0;
  const sessionHasText = (m) => Array.isArray(m.sessions) && m.sessions.some(
    (s) => hasText(s.transcript) || hasText(s.aiSummary?.summary),
  );
  const hasUsable = meetings.some((m) =>
    hasText(m.aiSummary?.summary) || hasText(m.transcript) || sessionHasText(m),
  );
  if (meetings.length === 0) return null;

  const summary = data?.summary;
  const generatedAt = data?.generatedAt;
  const meetingsIncluded = data?.meetingsIncluded || 0;
  const cachedError = data?.error;
  const hasSummary = !!summary;
  // If new meetings (or new summaries) came in after the rollup was generated,
  // flag it as stale so the user knows clicking pulls in the latest.
  const stale = hasSummary && meetingsIncluded < meetings.length;

  const fmtGenAt = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleString(); } catch { return ""; }
  };

  const onGenerate = async () => {
    try {
      await gen.mutateAsync();
      setOpen(true);
      toast.success("Master summary ready");
    } catch (err) {
      // Friendlier copy for the rate-limit case — raw "rate_limited" is
      // confusing for end users.
      const raw = err?.response?.data?.error || err.message || "Failed";
      const msg = raw === "rate_limited"
        ? "Too many summary requests. Try again in a minute."
        : raw;
      toast.error(msg);
    }
  };

  return (
    <div style={{
      background: "var(--crm-primary-soft)",
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 8, padding: 14,
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Sparkles size={16} style={{ color: "var(--crm-primary)", flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
              Master summary — all meetings
            </div>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
              {hasSummary ? (
                <>Based on {meetingsIncluded} meeting{meetingsIncluded === 1 ? "" : "s"} · generated {fmtGenAt(generatedAt)}{stale ? ` · ${meetings.length - meetingsIncluded} newer meeting(s) since` : ""}</>
              ) : !hasUsable ? (
                <>Sync transcripts or paste them on individual meetings first, then come back here.</>
              ) : (
                <>One click to combine all {meetings.length} meeting{meetings.length === 1 ? "" : "s"} into a single overview.</>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {hasSummary && (
            <button
              onClick={() => setOpen((o) => !o)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                padding: "6px 10px", borderRadius: 8, border: "none",
                background: "transparent", color: "var(--crm-primary)",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}
            >
              {open ? <><ChevronUp size={11} /> Hide</> : <><ChevronDown size={11} /> View</>}
            </button>
          )}
          <button
            onClick={onGenerate}
            disabled={gen.isPending || !hasUsable}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "6px 12px", borderRadius: 8, border: "none",
              background: "var(--crm-primary)", color: "#fff",
              fontSize: 11, fontWeight: 600,
              cursor: gen.isPending || !hasUsable ? "not-allowed" : "pointer",
              opacity: gen.isPending || !hasUsable ? 0.6 : 1,
            }}
            title={!hasUsable ? "At least one meeting needs a transcript or summary first" : ""}
          >
            {gen.isPending
              ? <><Loader2 size={11} className="animate-spin" /> Summarizing…</>
              : hasSummary
                ? <><Sparkles size={11} /> {stale ? "Refresh" : "Re-generate"}</>
                : <><Sparkles size={11} /> Summarize all meetings</>}
          </button>
        </div>
      </div>

      {cachedError && !gen.isPending && (
        <div style={{ fontSize: 11, color: "var(--crm-danger)" }}>{cachedError}</div>
      )}

      {open && hasSummary && (
        <div style={{
          padding: 12, borderRadius: 8,
          background: "var(--color-bg-surface)",
          border: "1px solid var(--color-border-subtle)",
          fontSize: 12, lineHeight: "20px", whiteSpace: "pre-wrap",
          color: "var(--color-text-primary)",
        }}>
          {summary}
        </div>
      )}
    </div>
  );
}

export default function MeetingsPanel({ leadId, lead, focusItemId, onFocusResolved }) {
  const projectId = lead?.projectId ? String(lead.projectId) : null;
  const { data: meetings = [], isLoading } = useLeadMeetings(leadId);
  // Upcoming first and soonest-first, so the next thing on the calendar is the
  // first thing you see. Done is newest-first because recent calls are the ones
  // you go back and read.
  const meetingGroups = useMemo(() => splitMeetingsByState(meetings), [meetings]);
  const { data: acct, isLoading: acctLoading } = useProjectMeetAccount(projectId);
  const { data: userAcct, isLoading: userAcctLoading } = useUserMeetAccount();
  const startUserConnect = useStartUserMeetConnect();
  const [modalOpen, setModalOpen] = useState(false);
  const autoSync = usePullVexaTranscript(leadId);
  const { register } = useFocusRow(focusItemId, { onResolved: onFocusResolved });
  // Track which meetings we've already auto-synced this mount so we don't
  // refetch on every render. Reset when leadId changes.
  const autoSyncedRef = useRef(new Set());

  // User's personal account takes priority over the project account — mirrors
  // the backend createForLead logic. Show whichever will actually be used.
  const effectiveAcct = userAcct?.connected ? userAcct : acct;
  const connected = !!effectiveAcct?.connected;
  const isPersonalAcct = !!userAcct?.connected;
  const canSchedule = !!projectId && connected;

  // Auto-sync on drawer open: only meetings that genuinely need it AND whose
  // bot is finished. CRITICAL: never auto-sync a meeting whose bot is still
  // 'requested'/'joining'/'recording' — sync stops the bot to finalise the
  // recording, and stopping a bot that's mid-join kills it before it can
  // capture anything. Only pull once the bot reports 'completed'.
  useEffect(() => {
    if (!meetings.length) return;
    for (const m of meetings) {
      if (m.status === "canceled" || !m.meetUri) continue;
      // Already have a transcript — nothing to fetch.
      if (m.transcript) continue;
      const botStatus = m.vexaBot?.status || "idle";
      // Only sync when the bot has fully completed. Active states are left
      // alone so the live recording isn't interrupted.
      if (botStatus !== "completed") continue;
      if (autoSyncedRef.current.has(m._id)) continue;
      autoSyncedRef.current.add(m._id);
      autoSync.mutate(m._id, { onError: () => {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetings]);

  // Reset auto-sync tracking when the lead changes — different lead = fresh
  // chance to re-sync its meetings.
  useEffect(() => {
    autoSyncedRef.current = new Set();
  }, [leadId]);

  // Same connect flow as Settings → My Account: any user can self-serve this,
  // no admin needed, so it belongs right where the "you can't schedule yet"
  // wall shows up instead of just pointing at a settings page to go find.
  const handleConnectGoogle = async () => {
    try {
      const res = await startUserConnect.mutateAsync();
      const url = res?.authUrl;
      if (!url) throw new Error("No auth URL returned");
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to start Google connect");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <style>{FOCUS_ROW_STYLE}</style>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Meetings</div>
          <Badge tone="neutral">{meetings.length}</Badge>
        </div>
        <Button
          size="sm"
          leftIcon={<Plus size={13} />}
          disabled={!canSchedule}
          onClick={() => setModalOpen(true)}
        >
          Schedule meeting
        </Button>
      </div>

      {!projectId && (
        <div style={{
          padding: 12, borderRadius: 8,
          background: "var(--crm-warning-soft, var(--color-bg-muted))",
          color: "var(--crm-warning, var(--color-text-secondary))",
          fontSize: 12, display: "flex", alignItems: "center", gap: 8,
        }}>
          <AlertCircle size={13} />
          Assign this lead to a project before scheduling meetings.
        </div>
      )}

      {projectId && !acctLoading && !userAcctLoading && !connected && (
        <div style={{
          padding: 12, borderRadius: 8,
          background: "var(--color-bg-muted)",
          fontSize: 12, color: "var(--color-text-primary)",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertCircle size={13} style={{ color: "var(--crm-warning, var(--color-text-secondary))" }} />
            <span>No Google account connected — meetings need one to schedule.</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <Button
              size="sm"
              leftIcon={<Video size={12} />}
              onClick={handleConnectGoogle}
              disabled={startUserConnect.isPending}
            >
              {startUserConnect.isPending ? "Opening Google…" : "Connect your Google account"}
            </Button>
            <span style={{ color: "var(--color-text-secondary)", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5 }}>
              <SettingsIcon size={11} /> or ask an admin to connect the shared project account in Settings → Projects.
            </span>
          </div>
        </div>
      )}

      {projectId && connected && (
        <div style={{
          padding: "8px 12px", borderRadius: 8,
          background: "var(--crm-success-soft, var(--color-bg-muted))",
          fontSize: 11, color: "var(--crm-success, var(--color-text-secondary))",
          display: "inline-flex", alignItems: "center", gap: 6, width: "fit-content",
        }}>
          <CheckCircle2 size={12} />
          {isPersonalAcct
            ? <>Your account · {effectiveAcct.email}</>
            : <>Project account · {effectiveAcct.email}</>
          }
        </div>
      )}

      {!isLoading && meetings.length > 0 && (
        <MeetingsRollupCard leadId={leadId} meetings={meetings} />
      )}

      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, color: "var(--color-text-secondary)", fontSize: 13 }}>
          <Loader2 size={13} className="animate-spin" /> Loading meetings…
        </div>
      )}

      {!isLoading && meetings.length === 0 && (
        <div style={{ textAlign: "center", padding: "32px 16px", color: "var(--color-text-secondary)" }}>
          <Video size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-text-primary)" }}>No meetings yet</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>
            {canSchedule ? "Click \"Schedule meeting\" to create one." : "Connect a Google account above to schedule meetings."}
          </div>
        </div>
      )}

      {meetingGroups.map((group) => (
        <div key={group.label} style={{ marginBottom: 14 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 6, marginBottom: 8,
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: 0.4, color: "var(--color-text-secondary)",
          }}>
            {group.label}
            <span style={{
              padding: "1px 7px", borderRadius: 999, fontSize: 10,
              background: "var(--color-bg-muted)", letterSpacing: 0,
            }}>
              {group.items.length}
            </span>
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
            {group.items.map((m) => (
              <MeetingRow key={m._id} leadId={leadId} meeting={m} rowRef={register(m._id)} />
            ))}
          </ul>
        </div>
      ))}

      <CreateMeetingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        leadId={leadId}
        lead={lead}
      />
    </div>
  );
}
